import { Component } from '@mycolorway/tao';
import ScrollMagic from 'scrollmagic';
import { closest } from 'next/javascripts/modules/dom';
import eventListenerMixin from 'next/javascripts/mixins/event-listener-mixin';
import 'next/stylesheets/components/document-toc.scss';

function transformHeadingTree(headings) {
  const result = {};
  const parentLevel = { 0: -1 };

  let parent;
  let prevLevel = { level: -1, index: -1, parent: -1 };

  headings.forEach((heading, index) => {
    const nodeName = heading.nodeName.toLowerCase();
    const currentLevel = parseInt(/^h(\d)$/.exec(nodeName)[1], 10);

    if (currentLevel > prevLevel.level) {
      parent = prevLevel.index;
    } else if (currentLevel < prevLevel.level) {
      parent = parentLevel[currentLevel] == null
        ? prevLevel.parent : parentLevel[currentLevel];
    } else {
      ({ parent } = prevLevel);
    }

    parentLevel[currentLevel] = parent;
    prevLevel = { level: currentLevel, index, parent };

    result[index] = {
      heading,
      depth: parent !== -1 && result[parent] ? result[parent].depth + 1 : 0,
    };
  });

  return result;
}

Component('tr-document-toc', {
  mixins: [eventListenerMixin],

  properties: {
    contentSelector: String,
    scrollContainer: String,
    renderTocOnReady: {
      type: Boolean,
      default: true,
    },
    empty: {
      type: Boolean,
      default: true,
    },
  },

  ready() {
    this.toc = this.querySelector('ul.toc-list');

    this.initScrollController();
    if (this.renderTocOnReady) this.generateToc();

    this.listenTo(this.toc, 'click', this.scrollToScene.bind(this));
  },

  update() {
    Object.keys(this.scrollScenes).forEach((id) => {
      const scene = this.scrollScenes[id];

      this.controller.removeScene(scene);
      scene.destroy();
      delete this.scrollScenes[id];
    });

    this.generateToc();
  },

  initScrollController() {
    const config = {};
    if (this.scrollContainer) {
      config.container = this.scrollContainer;
    }

    this.controller = new ScrollMagic.Controller(config);
    this.scrollScenes = {};
  },

  scrollToScene(event) {
    const tocItem = closest(event.target, '.toc-item');
    if (!tocItem) return;

    const scene = this.scrollScenes[tocItem.dataset.id];
    if (scene) {
      let scenePos = scene.scrollOffset();
      scenePos += scene.triggerElement().offsetHeight / 2;
      this.controller.scrollTo(scenePos);
    }
  },

  generateToc() {
    const content = document.querySelector(this.contentSelector);
    if (!content) return;

    const headingTree = transformHeadingTree(
      Array.from(content.querySelectorAll('h1, h2, h3, h4, h5')),
    );
    const tocList = document.createDocumentFragment();

    Object.keys(headingTree).forEach((index) => {
      const treeItem = headingTree[index];
      const tocItem = this.generateTocItem(treeItem, index);
      if (!tocItem) return;

      const { heading: nextHeading } = headingTree[parseInt(index, 10) + 1] || {};
      this.addScrollScene(treeItem.heading, nextHeading, tocItem);
      tocList.appendChild(tocItem);
    });

    this.toc.innerHTML = '';
    this.toc.appendChild(tocList);

    this.empty = this.toc.childElementCount === 0;
  },

  generateTocItem({ heading, depth }, treeItemIndex) {
    const title = heading.textContent.trim();
    if (!title) return null;

    const tocItem = document.createElement('li');

    tocItem.classList.add('toc-item', `toc-item-${depth}`);
    tocItem.dataset.id = `${treeItemIndex}-${title.replace(/[.-?#\s]/, 'g')}`;
    tocItem.innerHTML = `<a href="javascript:;">${title}</a>`;

    return tocItem;
  },

  addScrollScene(heading, nextHeading, tocItem) {
    const scene = new ScrollMagic.Scene({
      duration: nextHeading ? nextHeading.offsetTop - heading.offsetTop : '100%',
      triggerElement: heading,
      triggerHook: 0.4,
    });
    this.scrollScenes[tocItem.dataset.id] = scene;
    this.controller.addScene(scene);

    scene.on('enter', () => {
      Array.from(this.querySelectorAll('.toc-item.active'))
        .forEach(item => item.classList.remove('active'));
      tocItem.classList.add('active');
    });

    scene.on('leave', ({ scrollDirection }) => {
      if (scrollDirection === 'FORWARD' && !tocItem.nextSibling) {
        tocItem.classList.remove('active');
      } else if (scrollDirection === 'REVERSE') {
        tocItem.classList.remove('active');

        const adjacentItem = tocItem.previousSibling;
        if (adjacentItem) adjacentItem.classList.add('active');
      }
    });
  },

  disconnected() {
    if (this.controller) this.controller.destroy();
    this.controller = null;

    this.stopListening();
  },
});
